import { IToastType, ToastType } from './enums/toast-type';
import { ChangeDetectionStrategy, Component, HostBinding, OnInit } from '@angular/core';

/**
 * External Libraries
 */
import { Toast, ToastrService, ToastPackage } from 'ngx-toastr';

/**
 * Custom Directives
 */
import { WavesEffectDirective } from '@sharedDirectives/node-waves/waves-effect.directive';

/**
 * @component NotyfToastComponent
 * @description
 * A customizable and accessible toast notification styled like Notyf. Supports contextual coloring, icon injection,
 * and optional close buttons. It extends ngx-toastr's Toast base class for full compatibility.
 *
 * @example
 * ```ts
 * this.toastrService.show('Message', 'Title', { toastComponent: NotyfToastComponent });
 * ```
 */
@Component({
  selector: 'notyf-toast-component',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    WavesEffectDirective
  ],
  host: {
    class: 'notyf__toast text-white',
    '[class.notyf__toast--dismissible]': 'closeBtn',
  },
  templateUrl: './notyf-toast.component.html',
  styleUrls: ['./notyf-toast.component.scss'],
})
export class NotyfToastComponent extends Toast implements OnInit {

  /**
   * Type of toast (Success, Error, Info, Warning, Default)
   * Determined from `toastPackage.toastType`
   */
  public type!: IToastType;

  /**
   * Theme color used for styling the background via Bootstrap CSS variables.
   * Examples: 'success', 'danger', 'warning', etc.
   */
  public themeColor!: string;

  /**
   * Font Awesome icon class name to be shown with the toast.
   */
  public icon!: string;

  /**
   * Binds the toast background color dynamically using Bootstrap CSS variables.
   * @returns Computed color string like `var(--bs-success)`
   */
  @HostBinding('style.backgroundColor')
  public get styleColor(): string {
    return `var(--bs-${this.themeColor})`;
  }

  /**
   * Whether to show the dismiss button or not.
   * Controlled by `toastPackage.config.closeButton`
   */
  public get closeBtn(): boolean {
    return this.toastPackage.config.closeButton;
  }

  /**
   * Constructor injects the toastr service and toast package.
   * @param toastrService Instance of ToastrService
   * @param toastPackage Package containing toast configuration and content
   */
  constructor(
    public override toastrService: ToastrService,
    public override toastPackage: ToastPackage
  ) {
    super();
  }


  /**
   * @lifecycle OnInit
   * Initializes toast properties (type, icon, color) based on toastPackage.toastType
   */
  ngOnInit(): void {
    const toastType = this.toastPackage.toastType;

    const toastMap: Record<string, { type: IToastType; color: string; icon: string }> = {
      success: { type: ToastType.Success, color: 'success', icon: 'fa-circle-check' },
      error: { type: ToastType.Error, color: 'danger', icon: 'fa-circle-xmark' },
      info: { type: ToastType.Info, color: 'info', icon: 'fa-circle-info' },
      warning: { type: ToastType.Warning, color: 'warning', icon: 'fa-triangle-exclamation' },
      default: { type: ToastType.Default, color: 'primary', icon: 'fa-badge-check' },
    };

    const foundType = Object.keys(toastMap).find((key) => toastType.includes(key)) ?? 'default';

    const { type, color, icon } = toastMap[foundType];

    this.type = type;
    this.themeColor = color;
    this.icon = icon;
    
  }

}
